/**
 * The URL language prefixes, in one place.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * This list used to be copy-pasted into nine files: the Vite SSG route filter,
 * i18n.ts (twice), main.tsx, LangWrapper, BlogPostWrapper, DocsWrapper, the
 * sitemap generator, the SSG post-processor and one test. Copies drift, and
 * every drift so far has been the same shape and the same damage:
 *
 *  1. 2026-08-02, 'uk' and 'cs' missing from the Vite copy: /cs/admin and
 *     /uk/admin were prerendered as indexable HTML.
 *  2. Same audit, 'nl', 'hu', 'lt', 'ro', 'sv' missing from the same copy:
 *     five more languages' protected routes leaked the same way.
 *  3. 2026-08-26, the EU-24 sprint's nine locales missing from that copy
 *     again: /bg/tenders/map fell through the SSG exclusion list, SSG tried
 *     to prerender a page that imports Leaflet at module scope, and the build
 *     died on "window is not defined". The leak was there too, quietly.
 *
 * Three times, one root cause. The comment on the Vite copy said "no more, no
 * fewer" and that did not stop it, because a comment cannot be imported.
 *
 * THE RULE
 * --------
 * Nothing else declares this list. Everything imports it, including
 * `vite.config.ts` and the build scripts. That is why this module has NO
 * imports of its own and touches no browser or Node API: it has to be safe to
 * pull into a Vite config, a Node script and a browser bundle alike. Keep it
 * that way. `scripts/check-lang-prefix-parity.mjs` fails the build if a
 * literal copy reappears anywhere in the tree.
 *
 * English has no prefix; it is served from the unprefixed path.
 */
export const LANG_PREFIXES = [
  'pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th',
  'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt', 'ro', 'sv',
  // EU-24 sprint, 2026-08-25.
  'bg', 'da', 'el', 'et', 'fi', 'hr', 'lv', 'sk', 'sl',
] as const;

export type LangPrefix = (typeof LANG_PREFIXES)[number];

/** Membership test without rebuilding a Set at every call site. */
export const LANG_PREFIX_SET: ReadonlySet<string> = new Set<string>(LANG_PREFIXES);

/**
 * Is this path segment a language prefix?
 *
 * Exists because `LANG_PREFIXES.includes(segment)` does not type-check against
 * an `as const` tuple: TypeScript narrows the array's element type to the
 * literal union, so passing a plain `string` is an error. Every call site
 * would otherwise need its own cast, and a cast per call site is how a
 * `string[]` sneaks back in.
 */
export function isLangPrefix(segment: string): boolean {
  return LANG_PREFIX_SET.has(segment);
}

/**
 * The language a URL renders in: its prefix, or 'en' for an unprefixed path.
 *
 * Lives here rather than beside any one consumer because the four
 * hand-written copies of this three-line function were the second half of the
 * duplication: a correct list read by a subtly different splitter still
 * disagrees about what /pt-br/blog/x is.
 */
export function urlLangOf(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment && LANG_PREFIX_SET.has(segment) ? segment : 'en';
}

// Note: prefix STRIPPING deliberately stays where it is (LangWrapper for the
// app, inline in vite.config.ts for the SSG filter). The two differ on what an
// unprefixed path returns, several components depend on LangWrapper's exact
// shape, and quietly unifying them here would be a behaviour change smuggled
// into a de-duplication.
